import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { usePermission } from '../../contexts/PermissionContext';
import { FUNCTIONS_URL } from '../../firebase';
import { permissionTagsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import TagFormModal from './TagFormModal';
import ResourceModal from './ResourceModal';
import MultiTagModal from './MultiTagModal';

const PermissionTagManagement = () => {
  const { getAuthHeader } = useAuth();
  const { hasWildcardAccess, refreshPermissionTags, getVisibleTags } = usePermission();
  const isAdmin = hasWildcardAccess();
  const myVisibleTags = getVisibleTags();

  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [formLoading, setFormLoading] = useState(false);

  // 資源管理 Modal 狀態
  const [isResourceModalOpen, setIsResourceModalOpen] = useState(false);
  const [resourceTag, setResourceTag] = useState(null);
  const [resourceLoading, setResourceLoading] = useState(false);
  const [resourceSaving, setResourceSaving] = useState(false);
  const [allResources, setAllResources] = useState({ projects: [], reports: [] });
  const [selectedResources, setSelectedResources] = useState({ projects: [], reports: [] });
  const [activeResourceTab, setActiveResourceTab] = useState('projects');

  // 多標籤編輯 Modal 狀態
  const [isMultiTagModalOpen, setIsMultiTagModalOpen] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [editingResourceTags, setEditingResourceTags] = useState([]);

  const [formData, setFormData] = useState({
    id: '', name: '', description: '', color: '#3B82F6',
  });

  // 載入權限標籤列表
  const loadTags = useCallback(async () => {
    try {
      setLoading(true);
      const headers = await getAuthHeader();
      const params = new URLSearchParams();
      if (!isAdmin) params.append('filter_by_manageable', 'true');

      const response = await fetch(`${FUNCTIONS_URL}/api/permission-tags?${params}`, {
        headers: { ...headers, 'Content-Type': 'application/json' },
      });
      const result = await response.json();

      if (result.success) {
        setTags(result.data || []);
      } else {
        toast.error(result.error?.message || '載入權限標籤失敗');
      }
    } catch (error) {
      console.error('Error loading permission tags:', error);
      toast.error('載入權限標籤失敗');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, isAdmin]);

  useEffect(() => { loadTags(); }, [loadTags]);

  const openCreateModal = () => {
    setEditingTag(null);
    setFormData({ id: '', name: '', description: '', color: '#3B82F6' });
    setIsModalOpen(true);
  };

  const openEditModal = (tag) => {
    setEditingTag(tag);
    setFormData({ id: tag.id, name: tag.name, description: tag.description || '', color: tag.color || '#3B82F6' });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingTag(null);
    setFormData({ id: '', name: '', description: '', color: '#3B82F6' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      const headers = await getAuthHeader();
      const isEditing = !!editingTag;
      const url = isEditing
        ? `${FUNCTIONS_URL}/api/permission-tags/${editingTag.id}`
        : `${FUNCTIONS_URL}/api/permission-tags`;

      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: formData.id, name: formData.name, description: formData.description, color: formData.color }),
      });
      const result = await response.json();

      if (result.success) {
        toast.success(isEditing ? '權限標籤更新成功' : '權限標籤建立成功');
        closeModal();
        loadTags();
        refreshPermissionTags();
      } else {
        toast.error(result.error?.message || '操作失敗');
      }
    } catch (error) {
      console.error('Error saving permission tag:', error);
      toast.error('操作失敗');
    } finally {
      setFormLoading(false);
    }
  };

  const handleToggleActive = async (tag) => {
    const newStatus = !tag.is_active;
    const confirmMessage = newStatus
      ? `確定要啟用權限標籤「${tag.name}」嗎？`
      : `確定要停用權限標籤「${tag.name}」嗎？停用後，擁有此標籤的用戶將無法存取相關資料。`;
    if (!window.confirm(confirmMessage)) return;

    try {
      const headers = await getAuthHeader();
      const response = await fetch(`${FUNCTIONS_URL}/api/permission-tags/${tag.id}`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: newStatus }),
      });
      const result = await response.json();

      if (result.success) {
        toast.success(newStatus ? '權限標籤已啟用' : '權限標籤已停用');
        loadTags();
        refreshPermissionTags();
      } else {
        toast.error(result.error?.message || '操作失敗');
      }
    } catch (error) {
      console.error('Error toggling permission tag:', error);
      toast.error('操作失敗');
    }
  };

  const handleDeleteTag = async (tag) => {
    const confirmMessage = `確定要永久刪除權限標籤「${tag.name}」嗎？\n\n此操作將會：\n• 從所有專案移除此標籤\n• 從所有報表移除此標籤\n• 從所有用戶移除此權限\n\n此操作無法復原！`;
    if (!window.confirm(confirmMessage)) return;

    const doubleConfirm = window.prompt(`請輸入「${tag.name}」以確認刪除：`);
    if (doubleConfirm !== tag.name) {
      if (doubleConfirm !== null) toast.error('輸入的名稱不正確，操作已取消');
      return;
    }

    try {
      const headers = await getAuthHeader();
      const response = await fetch(`${FUNCTIONS_URL}/api/permission-tags/${tag.id}?permanent=true`, {
        method: 'DELETE',
        headers: { ...headers, 'Content-Type': 'application/json' },
      });
      const result = await response.json();

      if (result.success) {
        toast.success(result.message || '權限標籤已永久刪除');
        loadTags();
        refreshPermissionTags();
      } else if (result.error?.code === 'RESOURCES_WOULD_BECOME_ORPHANED') {
        const projects = result.error.orphan_projects || [];
        const reports = result.error.orphan_reports || [];
        const details = [
          ...projects.map(p => `專案：${p.name}`),
          ...reports.map(r => `報表：${r.name}`),
        ].join('\n');
        toast.error(`無法刪除：以下資源只剩這一個標籤，請先指派其他標籤\n${details}`, { duration: 8000 });
      } else {
        toast.error(result.error?.message || '刪除失敗');
      }
    } catch (error) {
      console.error('Error deleting permission tag:', error);
      toast.error('刪除失敗');
    }
  };

  const openResourceModal = async (tag) => {
    setResourceTag(tag);
    setIsResourceModalOpen(true);
    setResourceLoading(true);
    setActiveResourceTab('projects');

    try {
      const result = await permissionTagsAPI.getAvailableResources(tag.id);
      if (result.success) {
        setAllResources(result.data);
        const tagName = tag.name;
        setSelectedResources({
          projects: result.data.projects.filter(p => (p.access_tags || []).includes(tagName)).map(p => p.id),
          reports: result.data.reports.filter(r => (r.access_tags || []).includes(tagName)).map(r => r.id),
        });
      } else {
        toast.error(result.error?.message || '載入資源失敗');
      }
    } catch (error) {
      console.error('Error loading resources:', error);
      toast.error('載入資源失敗');
    } finally {
      setResourceLoading(false);
    }
  };

  const closeResourceModal = () => {
    setIsResourceModalOpen(false);
    setResourceTag(null);
    setAllResources({ projects: [], reports: [] });
    setSelectedResources({ projects: [], reports: [] });
  };

  const toggleResourceSelection = (type, id) => {
    setSelectedResources(prev => {
      const current = prev[type];
      if (current.includes(id)) {
        return { ...prev, [type]: current.filter(i => i !== id) };
      } else {
        return { ...prev, [type]: [...current, id] };
      }
    });
  };

  const saveResourceAssignments = async () => {
    if (!resourceTag) return;
    setResourceSaving(true);
    try {
      const tagName = resourceTag.name;
      const originalProjects = allResources.projects.filter(p => (p.access_tags || []).includes(tagName)).map(p => p.id);
      const originalReports = allResources.reports.filter(r => (r.access_tags || []).includes(tagName)).map(r => r.id);

      const data = {
        projects: { add: selectedResources.projects.filter(id => !originalProjects.includes(id)), remove: originalProjects.filter(id => !selectedResources.projects.includes(id)) },
        reports: { add: selectedResources.reports.filter(id => !originalReports.includes(id)), remove: originalReports.filter(id => !selectedResources.reports.includes(id)) },
      };

      const result = await permissionTagsAPI.updateResources(resourceTag.id, data);
      if (result.success) {
        toast.success('資源指派更新成功');
        closeResourceModal();
      } else {
        toast.error(result.error?.message || '更新失敗');
      }
    } catch (error) {
      console.error('Error saving resource assignments:', error);
      toast.error('更新失敗');
    } finally {
      setResourceSaving(false);
    }
  };

  const openMultiTagModal = (type, resource) => {
    setEditingResource({ type, resource });
    setEditingResourceTags(resource.access_tags || []);
    setIsMultiTagModalOpen(true);
  };

  const closeMultiTagModal = () => {
    setIsMultiTagModalOpen(false);
    setEditingResource(null);
    setEditingResourceTags([]);
  };

  const toggleEditingTag = (tagName) => {
    setEditingResourceTags(prev => prev.includes(tagName) ? prev.filter(t => t !== tagName) : [...prev, tagName]);
  };

  const saveMultiTagEditing = async () => {
    if (!editingResource) return;
    setResourceSaving(true);
    try {
      const { type, resource } = editingResource;
      const originalTags = resource.access_tags || [];
      const allTagNames = tags.map(t => t.name);
      const promises = [];

      for (const tagName of allTagNames) {
        const tagObj = tags.find(t => t.name === tagName);
        if (!tagObj) continue;
        const wasSelected = originalTags.includes(tagName);
        const isSelected = editingResourceTags.includes(tagName);

        if (wasSelected !== isSelected) {
          const collectionMap = { projects: 'projects', reports: 'reports' };
          const data = { [collectionMap[type]]: { add: isSelected ? [resource.id] : [], remove: !isSelected ? [resource.id] : [] } };
          promises.push(permissionTagsAPI.updateResources(tagObj.id, data));
        }
      }

      if (promises.length > 0) {
        await Promise.all(promises);
        toast.success('權限標籤更新成功');
        setAllResources(prev => ({
          ...prev,
          [type]: prev[type].map(r => r.id === resource.id ? { ...r, access_tags: editingResourceTags } : r),
        }));
      }
      closeMultiTagModal();
    } catch (error) {
      console.error('Error saving multi-tag editing:', error);
      toast.error('更新失敗');
    } finally {
      setResourceSaving(false);
    }
  };

  const getResourceTagBadge = (resource, currentTagName) => {
    const accessTags = resource.access_tags || [];
    const hasCurrentTag = accessTags.includes(currentTagName);
    const otherTags = accessTags.filter(t => t !== currentTagName);

    if (accessTags.length === 0) {
      return <span className="text-xs text-gray-400">未指派</span>;
    }

    return (
      <div className="flex flex-wrap gap-1">
        {hasCurrentTag && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">目前標籤</span>}
        {otherTags.slice(0, 2).map((tag) => (
          <span key={tag} className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">{tag}</span>
        ))}
        {otherTags.length > 2 && <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">+{otherTags.length - 2}</span>}
      </div>
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-800">權限標籤管理</h2>
          <p className="text-sm text-gray-500">管理系統中的權限標籤，用於控制資料存取範圍</p>
        </div>
        <button onClick={openCreateModal} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition flex items-center gap-2">
          <span>+</span><span>新增標籤</span>
        </button>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h3 className="font-semibold text-blue-900 mb-2">權限標籤說明</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>權限標籤用於區隔不同部門/區域的資料存取權限</li>
          <li>每個資源（專案、報表）都可綁定多個權限標籤</li>
          <li>用戶只能看到自己有權限標籤的資料</li>
          <li>停用標籤後，相關資料將無法被存取（建議謹慎操作）</li>
        </ul>
      </div>

      <div className="card overflow-hidden">
        {loading ? (
          <div className="text-center py-8 text-gray-500">載入中...</div>
        ) : tags.length === 0 ? (
          <div className="text-center py-8 text-gray-500">尚未建立任何權限標籤</div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">標籤</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">代碼</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">說明</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">狀態</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {tags.map((tag) => (
                <tr key={tag.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-3 py-1 rounded-full text-sm text-white font-medium" style={{ backgroundColor: tag.color || '#3B82F6' }}>{tag.name}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap"><code className="text-sm bg-gray-100 px-2 py-1 rounded">{tag.id}</code></td>
                  <td className="px-6 py-4 text-sm text-gray-500">{tag.description || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${tag.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {tag.is_active !== false ? '啟用' : '停用'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button onClick={() => openResourceModal(tag)} className="text-blue-600 hover:text-blue-900 mr-3">管理資源</button>
                    <button onClick={() => openEditModal(tag)} className="text-purple-600 hover:text-purple-900 mr-3">編輯</button>
                    <button onClick={() => handleToggleActive(tag)} className={tag.is_active !== false ? 'text-orange-600 hover:text-orange-900' : 'text-green-600 hover:text-green-900'}>
                      {tag.is_active !== false ? '停用' : '啟用'}
                    </button>
                    <button onClick={() => handleDeleteTag(tag)} className="text-red-600 hover:text-red-900 ml-3">刪除</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {isModalOpen && (
        <TagFormModal
          editingTag={editingTag}
          formData={formData}
          setFormData={setFormData}
          formLoading={formLoading}
          onSubmit={handleSubmit}
          onClose={closeModal}
        />
      )}

      {isResourceModalOpen && resourceTag && (
        <ResourceModal
          resourceTag={resourceTag}
          resourceLoading={resourceLoading}
          resourceSaving={resourceSaving}
          allResources={allResources}
          selectedResources={selectedResources}
          activeResourceTab={activeResourceTab}
          setActiveResourceTab={setActiveResourceTab}
          toggleResourceSelection={toggleResourceSelection}
          getResourceTagBadge={getResourceTagBadge}
          openMultiTagModal={openMultiTagModal}
          saveResourceAssignments={saveResourceAssignments}
          onClose={closeResourceModal}
        />
      )}

      {isMultiTagModalOpen && editingResource && (
        <MultiTagModal
          editingResource={editingResource}
          editingResourceTags={editingResourceTags}
          tags={tags}
          resourceSaving={resourceSaving}
          toggleEditingTag={toggleEditingTag}
          saveMultiTagEditing={saveMultiTagEditing}
          onClose={closeMultiTagModal}
        />
      )}
    </div>
  );
};

export default PermissionTagManagement;
